import { DataObject } from "@mui/icons-material"
import { LoadingButton } from "@mui/lab"
import { Box, Card, IconButton, TextField, Tooltip, Typography } from "@mui/material"
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useLocalStorage } from "react-use"
import { getDeviceIdAbsentWarning } from "../../../core/utils/constants"
import { defaultFrom, defaultTo, isValidRange, MAX_RANGE_MS } from "./dateRange"
import { filterRowsByQuery } from "./queryParser"

dayjs.extend(utc)

const QUERY_HINT = "Supported oprators: AND, OR, NOT, (). Example: \"session_start AND (level OR quest) NOT tutorial\""
const TODAY_STALE_SECONDS = 30

const PlayerEventsPage = ({
    useQuery,
    storageKey,
    initialSettings,
    typeField,
    typeHeaderName,
    DialogComponent
}) => {
    const [, setSettings] = useLocalStorage(storageKey, initialSettings)

    const apiRef = useGridApiRef()

    const deviceId = useSelector((state) => state?.data?.CommonData?.deviceId ?? "")

    const [dialogIsOpen, setDialogIsOpen] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null)
    const [searchQuery, setSearchQuery] = useState("")
    const [to, setTo] = useState(dayjs.utc())
    const [from, setFrom] = useState(to.subtract(2, "d"))
    const [appliedFrom, setAppliedFrom] = useState(from)
    const [appliedTo, setAppliedTo] = useState(to)

    const columns = useMemo(() => [
        {
            field: "id",
            headerName: "ID"
        },
        {
            field: "batchId",
            headerName: "Batch ID"
        },
        {
            field: "sn",
            headerName: "SN",
            width: 120,
            hideable: false
        },
        {
            field: "batchTimestamp",
            headerName: "Batch Timestamp",
            width: 185,
            hideable: false,
            valueGetter: (value) => dayjs.utc(value).format("DD.MM.YYYY HH:mm:ss.SSS")
        },
        {
            field: "timestamp",
            headerName: "Timestamp",
            width: 185,
            hideable: false,
            valueGetter: (value) => value ? dayjs.utc(value).format("DD.MM.YYYY HH:mm:ss.SSS") : "-"
        },
        {
            field: typeField,
            headerName: typeHeaderName,
            width: 240,
            hideable: false,
            valueGetter: (value) => value ? value : "-"
        },
        {
            field: "session_version",
            headerName: "Ver.",
            width: 80,
            hideable: false,
            valueGetter: (value) => value ? value : "-"
        },
        {
            field: "actions",
            headerName: "JSON",
            width: 61,
            hideable: false,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => (
                <IconButton onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRow(row)
                }}>
                    <DataObject/>
                </IconButton>
            )
        },
        {
            field: "quickLook",
            headerName: "Quick Look",
            flex: 1,
            hideable: false,
            sortable: false,
            filterable: false,
            valueGetter: (_, row) => {
                if (!Object.hasOwn(row, typeField)) return ""
                const settings = JSON.parse(localStorage.getItem(storageKey))
                if (settings === null || !Object.hasOwn(settings, row[typeField])) return ""
                const typeSettings = settings[row[typeField]]
                return Object
                    .entries(row)
                    .filter(([key]) => typeSettings.includes(key))
                    .map(([key, value]) => `${key} = ${value}`)
                    .join(" | ")
            }
        }
    ], [storageKey, typeField, typeHeaderName])

    const rangeValid = isValidRange(appliedFrom, appliedTo)
    const includesNow = rangeValid && !appliedTo.isBefore(dayjs.utc().subtract(1, "minute"))

    const { data: rawData, isLoading, isFetching } = useQuery(
        {
            userId: deviceId,
            from: appliedFrom?.toISOString?.(),
            to: appliedTo?.toISOString?.()
        },
        {
            skip: deviceId === "" || !rangeValid,
            refetchOnMountOrArgChange: includesNow ? TODAY_STALE_SECONDS : false
        }
    )

    const data = useMemo(
        () => (rawData ?? []).map((event, idx) => ({ ...event, id: idx })),
        [rawData]
    )

    const handleFilter = useCallback(() => {
        if (!isValidRange(from, to)) return
        setAppliedFrom(from)
        setAppliedTo(to)
    }, [from, to])

    const handleFromChange = useCallback((newFrom) => {
        if (!newFrom?.isValid?.()) return
        setFrom(newFrom)
        const diff = newFrom.diff(to, "d", true)
        if (diff < -2) {
            setTo(newFrom.add(2, "d"))
        } else if (diff >= 0) {
            setTo(newFrom.add(1, "h"))
        }
    }, [to])

    const handleToChange = useCallback((newTo) => {
        if (!newTo?.isValid?.()) return
        setTo(newTo)
        const diff = newTo.diff(from, "d", true)
        if (diff > 2) {
            setFrom(newTo.subtract(2, "d"))
        } else if (diff <= 0) {
            setFrom(newTo.subtract(1, "h"))
        }
    }, [from])

    const filteredRows = useMemo(() => {
        return filterRowsByQuery(data, searchQuery)
    }, [data, searchQuery])

    useEffect(() => {
        setDialogIsOpen(selectedRow !== null)
    }, [selectedRow])

    const handleRowClick = useCallback(({ row }) => {
        setTimeout(() => {
            apiRef.current?.scrollToIndexes({
                colIndex: 0,
                rowIndex: row.id
            })
        }, 100)
    }, [apiRef])

    const queryIsActive = searchQuery.trim().length > 0

    if (deviceId === "") return getDeviceIdAbsentWarning()

    return (
        <>
            <Box sx={{ width: "100%", height: "68vh" }}>
                <Card sx={{ padding: 2, display: "flex", flexDirection: "column", rowGap: 1 }}>
                    <Box sx={{ display: "flex", flexDirection: "row", columnGap: 1, alignItems: "center" }}>
                        <Tooltip title={QUERY_HINT} placement="bottom-start">
                            <TextField
                                sx={{ flex: "0 0 40%" }}
                                type="text"
                                size="small"
                                label="Search (AND / OR / NOT / скобки)"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="level_start AND (quest OR tutorial) NOT daily"
                            />
                        </Tooltip>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label="From (UTC)"
                                value={from}
                                onChange={handleFromChange}
                                timezone="UTC"
                                format="DD.MM.YYYY HH:mm"
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { flex: "0 0 22%" }
                                    }
                                }}
                            />
                            <DateTimePicker
                                label="To (UTC)"
                                value={to}
                                onChange={handleToChange}
                                timezone="UTC"
                                format="DD.MM.YYYY HH:mm"
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { flex: "0 0 22%" }
                                    }
                                }}
                            />
                        </LocalizationProvider>

                        <LoadingButton
                            variant="contained"
                            size="small"
                            onClick={handleFilter}
                            loading={isFetching}
                            disabled={!isValidRange(from, to)}
                        >
                            Filter
                        </LoadingButton>
                    </Box>
                    {queryIsActive && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                pl: 0.5
                            }}>
                            Показано {filteredRows.length} из {data.length} строк
                        </Typography>
                    )}
                </Card>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                    <Box
                        component="a"
                        onClick={(e) => {
                            e.preventDefault()
                            setSettings(initialSettings)
                        }}
                        sx={{
                            textDecoration: "underline",
                            textDecorationStyle: "dashed",
                            cursor: "pointer",
                            color: "inherit",
                            "&:hover": {
                                opacity: 0.7
                            }
                        }}
                    >
                        Default Quick Look
                    </Box>
                </Box>

                <DataGridPro
                    disableAutosize
                    disableColumnResize
                    disableRowSelectionOnClick
                    slotProps={{
                        loadingOverlay: {
                            variant: "linear-progress",
                            noRowsVariant: "skeleton"
                        }
                    }}
                    apiRef={apiRef}
                    loading={isLoading || isFetching}
                    columns={columns}
                    columnVisibilityModel={{
                        id: false,
                        batchId: false
                    }}
                    rows={filteredRows}
                    getRowHeight={() => "auto"}
                    onRowClick={handleRowClick}
                    pagination={false}
                    pageSizeOptions={[]}
                />
            </Box>
            <DialogComponent
                open={dialogIsOpen}
                onClose={() => setSelectedRow(null)}
                playerEvent={selectedRow}
                storageKey={storageKey}
            />
        </>
    )
}

export default PlayerEventsPage
